import { purchaseStatusValues } from "@/api/article";
import { FullShopOrder, useUpdateOrder } from "@/api/order";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import { Form, FormField } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { SetStateAction, useEffect, useId, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { object, z } from "zod";
import { ArticleOrderRecap } from "./ArticleOrderRecap";
import { CustomerInfoRecap } from "./CustomerInfoRecap";
import { PurchaseStatusBadge } from "./PurchaseStatusBadge";

const schema = object({
  purchaseStatus: z.enum(purchaseStatusValues),
});

export interface OrderEditDrawerProps {
  order: FullShopOrder;
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
}

export const OrderEditDrawer = ({
  order,
  open,
  setOpen,
}: OrderEditDrawerProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const defaultValues = useMemo(
    () => ({
      purchaseStatus: order.purchaseStatus,
    }),
    [order],
  );

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [order, form, defaultValues]);

  const orderMutator = useUpdateOrder();

  const editOnSubmit = (values: z.infer<typeof schema>) => {
    orderMutator.mutate(
      {
        orderId: order.id,
        update: values,
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("editOrderSucessMessage"),
            duration: 1000,
            variant: "success",
          });

          form.reset(defaultValues);
          setOpen(false);
        },
        onError: () =>
          toast({
            title: t("error"),
            description: t("editOrderErrorMessage"),
            duration: 1000,
            variant: "error",
          }),
      },
    );
  };

  return (
    <StyledDrawer
      title={
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold">{t("sale")}</h3>
          <span className="text-gray-600">
            {new Date(order.issuedAt).toLocaleDateString("fr")}
          </span>
        </div>
      }
      footer={
        <CustomDialogFooter>
          <Button
            disabled={!form.formState.isDirty || !form.formState.isValid}
            className="w-full font-semibold"
            form={formId}
          >
            {t("saveStatus")}
          </Button>
        </CustomDialogFooter>
      }
      className="md:max-w-[575px]"
      sheetProps={{
        open: open,
        onOpenChange: setOpen,
      }}
      description="description"
    >
      <Form {...form}>
        <form
          className="flex flex-col gap-6"
          onSubmit={form.handleSubmit(editOnSubmit)}
          id={formId}
        >
          {order.items.map((i) => (
            <ArticleOrderRecap key={i.relatedArticle.id} item={i} />
          ))}

          <CustomerInfoRecap
            customer={order.customer}
            paymentMethod={order.paymentMethod}
          />
          <FormField
            control={form.control}
            name="purchaseStatus"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-transparent pl-0 shadow-none">
                  <PurchaseStatusBadge
                    className="h-9 w-full"
                    status={field.value}
                  />
                </SelectTrigger>
                <SelectContent>
                  {purchaseStatusValues.map((psv) => (
                    <SelectItem
                      className="w-full block px-0"
                      key={psv}
                      value={psv}
                    >
                      <PurchaseStatusBadge
                        className="w-full h-9"
                        status={psv}
                      />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </form>
      </Form>
    </StyledDrawer>
  );
};
